import { useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GetCollectionsShowResponse, GetScreensShowResponseSectionItems, MediaItem } from 'mytours-read-api-typescript'
import {
	RootStoreState,
	loadCollectionDetails,
	generateCollectionVersion,
	getCurrentLanguageCode,
} from 'mytours-read-common'

const RECENT_COUNT = 4

export interface PurchasedItem {
	key: string // `${itemId}-${itemType}` — matches the favourites keying
	itemId: number
	itemType: string // 'collection' | 'screen'
	// Long-form subtype for onLinkAction (e.g. 'collection_tour', 'screen_story'). Bundle items
	// already carry it; for owned collections we derive it from the PascalCase Collection.subtype.
	subtype: string
	title?: string
	coverImage?: MediaItem
	version?: number
	accountId?: number
	addedAt?: number
}

// 'CollectionTour' -> 'collection_tour' (the form onLinkAction expects). An explicit, exhaustive
// map rather than string munging: onLinkAction silently no-ops on an unknown subtype, so a new
// enum value must fail compilation here instead of producing a dead card.
const SUBTYPE_TO_LINK: Record<
	GetCollectionsShowResponse.SubtypeEnum,
	GetScreensShowResponseSectionItems.ItemSubtypeEnum
> = {
	[GetCollectionsShowResponse.SubtypeEnum.CollectionList]:
		GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionList,
	[GetCollectionsShowResponse.SubtypeEnum.CollectionTour]:
		GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionTour,
	[GetCollectionsShowResponse.SubtypeEnum.CollectionOrganization]:
		GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionOrganization,
	[GetCollectionsShowResponse.SubtypeEnum.CollectionMenu]:
		GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionMenu,
	[GetCollectionsShowResponse.SubtypeEnum.CollectionSearch]:
		GetScreensShowResponseSectionItems.ItemSubtypeEnum.CollectionSearch,
}

const subtypeToLink = (subtype?: GetCollectionsShowResponse.SubtypeEnum): string =>
	(subtype && SUBTYPE_TO_LINK[subtype]) || ''

/**
 * Purchased Content read model. Joins the live ownership state — code-redeemed/purchased
 * collections (collectionownership) and the items inside redeemed/purchased bundles
 * (redeemedBundles) — with cached render metadata (collectionDetails / the bundle items
 * themselves), sorted by addedAt desc. Nothing is duplicated: a refunded/revoked entry leaves the
 * source slice and drops out here automatically.
 *
 * Fetch-on-miss: an owned collection whose details aren't cached yet (e.g. never opened) has its
 * details loaded so a card can be drawn; the version comes from the app manifest (fallback to a
 * generated version), mirroring the collection list container.
 */
export const usePurchasedContent = (): { items: PurchasedItem[]; recent: PurchasedItem[] } => {
	const dispatch = useDispatch()
	const ownedCollections = useSelector((state: RootStoreState) => state.collectionownership.ownedCollections)
	const collectionDetails = useSelector((state: RootStoreState) => state.collectionlist.collectionDetails)
	const redeemedBundles = useSelector((state: RootStoreState) => state.bundles.redeemedBundles)
	const bundleProducts = useSelector((state: RootStoreState) => state.bundles.bundleProducts)
	const appManifest = useSelector((state: RootStoreState) => state.settings.appManifest)
	const language = useSelector(getCurrentLanguageCode)

	// Manifest lookup shared by the fetch effect and the items memo.
	const manifestCollectionsById = useMemo(
		() =>
			new Map(
				(appManifest?.collections ?? []).filter(item => item.id !== undefined).map(item => [item.id as number, item]),
			),
		[appManifest],
	)

	// Requests this hook instance has already made, keyed `${language}:${id}` — a language change
	// resets the details cache, and the language-scoped key lets the new language refetch even if
	// the wipe itself was missed (it can land while the cache is still empty). Needed because the
	// redux loading flag only appears after the saga's async disk check, and a failed fetch's
	// error is never cleared — without this, a failure would refetch in a loop and rapid
	// ownership updates would dispatch duplicates.
	const requestedRef = useRef<Set<string>>(new Set())
	const prevDetailsSizeRef = useRef(0)

	// Load details for owned collections that aren't cached yet.
	useEffect(() => {
		if (collectionDetails.size === 0 && prevDetailsSizeRef.current > 0) {
			// The details cache was wiped same-language (rental-mode reset). Forget what we've
			// requested so the wipe doesn't leave ids permanently unfetchable — Ionic keeps pages
			// mounted, so this ref can outlive the cache by a long way. Only the populated→empty
			// transition counts: the cache is also empty during boot while requests are in flight,
			// and clearing then would re-open the duplicate-dispatch window this set closes.
			requestedRef.current.clear()
		}
		prevDetailsSizeRef.current = collectionDetails.size
		Object.keys(ownedCollections).forEach(idStr => {
			if (!ownedCollections[idStr]) {
				return
			}
			const id = Number(idStr)
			const cached = collectionDetails.get(id)
			const requestKey = `${language}:${id}`
			if (cached?.details || cached?.loading || requestedRef.current.has(requestKey)) {
				return
			}
			requestedRef.current.add(requestKey)
			const manifestItem = manifestCollectionsById.get(id)
			dispatch(
				loadCollectionDetails({
					id,
					version: manifestItem?.version ?? generateCollectionVersion(),
					accountId: manifestItem?.account_id,
					language,
				}),
			)
		})
	}, [ownedCollections, collectionDetails, manifestCollectionsById, language, dispatch])

	const items = useMemo<PurchasedItem[]>(() => {
		const byKey = new Map<string, PurchasedItem>()

		// Purchased / code-redeemed collections (render from cached details; skip until fetched).
		Object.keys(ownedCollections).forEach(idStr => {
			const entry = ownedCollections[idStr]
			if (!entry) {
				return
			}
			const id = Number(idStr)
			const details = collectionDetails.get(id)?.details
			if (!details) {
				return
			}
			const manifestItem = manifestCollectionsById.get(id)
			byKey.set(`${id}-collection`, {
				key: `${id}-collection`,
				itemId: id,
				itemType: 'collection',
				subtype: subtypeToLink(details.subtype),
				title: details.title,
				coverImage: details.cover_image_grid ?? details.cover_image,
				version: manifestItem?.version,
				accountId: manifestItem?.account_id,
				addedAt: entry.addedAt,
			})
		})

		// Items inside redeemed / purchased bundles (metadata is already in the slice).
		Object.keys(redeemedBundles).forEach(bundleId => {
			const bundle = redeemedBundles[bundleId]
			if (!bundle?.hasUnlockedBundle) {
				return
			}
			// As in BundleRedeemDialog: the entry's own copy cannot cover a bundle only fetchBundle reaches.
			const bundleItems = bundle.items ?? bundleProducts[bundleId]?.items
			bundleItems?.forEach(item => {
				if (!item.item_id || !item.item_type) {
					return
				}
				const key = `${item.item_id}-${item.item_type}`
				// A collection owned both directly and via a bundle: keep whichever is more recent.
				const existing = byKey.get(key)
				if (existing && (existing.addedAt ?? 0) >= (bundle.addedAt ?? 0)) {
					return
				}
				byKey.set(key, {
					key,
					itemId: item.item_id,
					itemType: item.item_type,
					subtype: item.item_subtype as string,
					title: item.title,
					coverImage: item.cover_image,
					version: item.item_version,
					addedAt: bundle.addedAt,
				})
			})
		})

		// Most-recent first; legacy entries without a timestamp sort last.
		return Array.from(byKey.values()).sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0))
	}, [ownedCollections, collectionDetails, redeemedBundles, bundleProducts, manifestCollectionsById])

	return { items, recent: items.slice(0, RECENT_COUNT) }
}
